import { useQuery } from "@apollo/client";
import Check from "@mui/icons-material/Check";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  InputAdornment,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import sortBy from "lodash-es/sortBy";
import { DateTime } from "luxon";
import { ReactElement, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";

import {
  CampaignSwitcherContactFieldsFragment,
  GetThreadCampaignContactsForSwitcherQuery,
  GetThreadCampaignContactsForSwitcherQueryVariables,
} from "~frontend/__generated__/graphql";
import ErrorCard from "~frontend/components/ErrorCard";
import { FlexBox } from "~frontend/components/FlexBox";
import Loading from "~frontend/components/Loading";
import { GET_THREAD_CAMPAIGN_CONTACTS_FOR_SWITCHER } from "~frontend/graphql/campaignContacts";
import { useIntl } from "~frontend/providers/IntlProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";

import lastMessagedTime from "./lastMessagedTime";
import { Thread } from "./types";

type SwitcherCampaign = {
  campaign: CampaignSwitcherContactFieldsFragment["campaign"];
  lastMessagedTime: number | null;
};

export default function ActiveCampaignSwitcherList({
  selectedCampaignId,
  setSelectedCampaignId,
  thread,
}: {
  selectedCampaignId: string | null;
  setSelectedCampaignId: (newCampaignId: string | null) => void;
  thread: Thread;
}): ReactElement {
  const { subaccount } = useSubaccount()!;
  const intl = useIntl();

  const campaignContactIds = useMemo(
    () =>
      Array.from(
        new Set(
          thread.messages
            .filter(m => m.campaignContactId)
            .map(m => m.campaignContactId!)
        )
      ),
    [thread.messages]
  );

  const { data, error, loading } = useQuery<
    GetThreadCampaignContactsForSwitcherQuery,
    GetThreadCampaignContactsForSwitcherQueryVariables
  >(GET_THREAD_CAMPAIGN_CONTACTS_FOR_SWITCHER, {
    variables: {
      input: {
        subaccountId: subaccount.id,
        campaignContactIds,
      },
    },
  });

  const [searchText, setSearchText] = useState("");

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  let rows: SwitcherCampaign[] = sortBy(
    data!.getCampaignContacts.map(cc => ({
      campaign: cc.campaign,
      lastMessagedTime: lastMessagedTime(cc.id, thread.messages),
    })),
    r => (r.lastMessagedTime ? -r.lastMessagedTime : Number.MAX_SAFE_INTEGER)
  );
  const normalizedSearchText = searchText.trim().toLowerCase();
  if (normalizedSearchText !== "") {
    rows = rows.filter(
      row =>
        row.campaign.id === normalizedSearchText ||
        row.campaign.name.toLowerCase().includes(normalizedSearchText)
    );
  }

  return (
    <Box
      sx={{
        maxHeight: "50vh",
        overflowY: "auto",
        backgroundColor: palette.lightestGray,
        padding: "24px",
      }}
    >
      <TextField
        fullWidth
        value={searchText}
        onChange={evt => setSearchText(evt.target.value)}
        placeholder={intl.formatMessage({
          defaultMessage: "Search for a campaign",
          description: "Placeholder for campaign filter",
        })}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton>
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Box>
        {rows.map(({ campaign, lastMessagedTime }) => (
          <Box
            sx={{
              borderBottom: `1px solid ${palette.lightGray}`,
              padding: "16px 0",
              fontSize: "10px",
            }}
          >
            <FlexBox>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ fontSize: "12px" }}>{campaign.name}</Box>
                <Box>{campaign.description}</Box>
              </Box>
              <Box sx={{ paddingLeft: "16px" }}>
                {campaign.id === selectedCampaignId ? (
                  <Button
                    variant="contained"
                    disabled
                    sx={{
                      fontSize: "10px",
                      width: "50px",
                      "&.Mui-disabled": { backgroundColor: palette.darkGreen },
                    }}
                  >
                    <Check sx={{ color: "white" }} />
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    sx={{
                      fontSize: "10px",
                      width: "50px",
                      backgroundColor:
                        selectedCampaignId === null
                          ? "primary"
                          : palette.lightGray,
                      color:
                        selectedCampaignId === null ? "white" : palette.navy,
                      "&:hover": {
                        color: "white",
                      },
                    }}
                    onClick={() => setSelectedCampaignId(campaign.id)}
                  >
                    <FormattedMessage
                      defaultMessage="Select"
                      description="Button text to select a campaign in campaign switcher"
                    />
                  </Button>
                )}
              </Box>
            </FlexBox>
            <FlexBox sx={{ marginTop: "8px" }}>
              <Box sx={{ flex: 1 }}>
                <strong>
                  <FormattedMessage
                    defaultMessage="Last Messaged"
                    description="Label for campaign start time in campaign switcher"
                  />
                  :{" "}
                </strong>
                {lastMessagedTime &&
                  DateTime.fromMillis(lastMessagedTime).toLocaleString(
                    DateTime.DATE_SHORT
                  )}
              </Box>
              <Box sx={{ flex: 1 }}>
                <strong>
                  <FormattedMessage
                    defaultMessage="Started"
                    description="Label for campaign start time in campaign switcher"
                  />
                  :{" "}
                </strong>
                {DateTime.fromMillis(campaign.startedAt).toLocaleString(
                  DateTime.DATE_SHORT
                )}
              </Box>
            </FlexBox>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
